import ExportDropdown, {
  ExportOption,
} from "@/components/design-system/export-dropdown";
import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import { Table, type TableColumn } from "@/components/design-system/table";
import {
  fetchSubmissions,
  selectNoSubmissions,
  selectSubmissionLoading,
  selectSubmissions,
  selectTotalItems,
} from "@/store/slices/submissionSlice";
import { AppDispatch } from "@store";
import { Eye } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
// import ViewAssignmentModal from "./view-assignment-modal";

// Using the SchoolModel from types
import { Badge } from "@/components/design-system/badge";
import FilterWithRadio from "@/components/design-system/FilterWithRadio";
import StudentTypeSelect from "@/components/design-system/StudentTypeSelect";
import {
  getAssignResourceDropdownV2Service,
  mapCsListDropdownToGradeOptions,
} from "@/service/base.service";
import { downloadSubmissionService } from "@/service/submission.service";
import { selectUser, selectUserRole } from "@/store/slices/authSlice";
import { setNavigationSource } from "@/store/slices/courseSlice";
import {
  DownloadSubmissionQuery,
  SubmissionModel,
} from "@/types/submission.model";
import {
  capitalizeFirstLetter,
  CSVIcon,
  downloadFileFromBlob,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  hasRolePermissions,
  PDFIcon,
  ROLE_PERMISSIONS,
  successToast,
} from "@/utils/constants";
import { toRecordFilterQuery } from "@/utils/assignmentRecordParams";
import type { StudentListType } from "@/types/student.model";
import {
  getSubmissionAssignType,
  getSubmissionResourceName,
  getSubmissionStatusLabel,
  getSubmissionViewPath,
  isSubmissionAssignmentObject,
} from "@/utils/submission.utils";

// Extended interface for UI display purposes
interface SubmissionDisplay extends SubmissionModel {
  sno?: number;
}

// Removed static schoolsData as we'll fetch from API

const ITEMS_PER_PAGE = 10; // Or any other value you prefer

export default function SubmissionListSection() {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [grade, setGrade] = useState("");
  const [selectedListType, setSelectedListType] =
    useState<StudentListType>("all");
  const [grades, setGrades] = useState<{ id: string; label: string }[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const submissions = useSelector(selectSubmissions);
  const loading = useSelector(selectSubmissionLoading);
  // const actionLoading = useSelector(selectSubmissionActionLoading);
  const totalItems = useSelector(selectTotalItems);
  const userRole = useSelector(selectUserRole);
  const user = useSelector(selectUser);
  const noSubmissions = useSelector(selectNoSubmissions);

  const columns: TableColumn<SubmissionDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] text-[16px] font-medium text-center text-borderBlack">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      key: "id",
      title: "Unique ID",
      dataIndex: "id",
      minWidth: 145,
      maxWidth: 145,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
        >
          {record.student.regno}
        </div>
      ),
    },
    {
      key: "name",
      title: "Student Name",
      dataIndex: "name",
      minWidth: 250,
      maxWidth: 250,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
        >
          {record.student.name}
        </div>
      ),
    },
    {
      key: "grade",
      title: "Grade",
      minWidth: 148,
      maxWidth: 148,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
        >
          {getSubmissionResourceName(record)}
        </div>
      ),
    },
    {
      key: "type",
      title: "Type",
      minWidth: 200,
      maxWidth: 200,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
        >
          {record.type === "QUERY"
            ? "Queries"
            : getSubmissionAssignType(record.assignment) === "GENERAL"
            ? "General Assignment"
            : "Unit Assignment"}
        </div>
      ),
    },
    {
      key: "lesson",
      title: "Lesson Name",
      minWidth: 218,
      maxWidth: 218,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
        >
          {record.type === "QUERY"
            ? record?.lesson?.name
            : (record.assignment as { lesson: { name: string } })?.lesson?.name}
        </div>
      ),
    },
    {
      key: "unit",
      title: "Unit Name",
      minWidth: 249,
      maxWidth: 249,
      sortable: true,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
        >
          {record.type === "QUERY"
            ? record?.unit?.name
            : (record.assignment as { unit: { name: string } })?.unit?.name}
        </div>
      ),
    },
    {
      key: "status",
      title: "Submission Status",
      minWidth: 204,
      maxWidth: 204,
      sortable: true,
      render: (_, record) => {
        const status = getSubmissionStatusLabel(record.status);

        return (
          <Badge
            variant={
              status === "NEW"
                ? "primary"
                : status === "REVIEWED"
                ? "warning"
                : "danger"
            }
          >
            {capitalizeFirstLetter(status)}
          </Badge>
        );
      },
    },
    {
      key: "date_time",
      title: "Submission Date & Time",
      minWidth: 276,
      maxWidth: 276,
      sortable: true,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.created_date}
        </div>
      ),
    },
  ];

  if (
    hasRolePermissions(userRole?.permissions as string[], [
      ROLE_PERMISSIONS.ACTION_VIEW_SUBMISSION,
    ])
  ) {
    columns.push({
      key: "action",
      title: "Action",
      minWidth: 140,
      maxWidth: 140,
      render: (_, record) => (
        <div className="flex items-center justify-start space-x-2">
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_VIEW_SUBMISSION,
            userRole?.permissions as string[]
          ) && (
            <button
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              title="View"
              onClick={() => handleViewSubmission(record)}
            >
              <Eye className="w-6 h-6 text-[#1C1B1F]" />
            </button>
          )}
        </div>
      ),
    });
  }

  const handleViewSubmission = async (record: SubmissionDisplay) => {
    const path = getSubmissionViewPath(record);

    if (!path) {
      return;
    }

    dispatch(setNavigationSource("submission-list"));

    const assignType = isSubmissionAssignmentObject(record.assignment)
      ? getSubmissionAssignType(record.assignment)
      : undefined;

    navigate(path, {
      state: {
        selectedTab: record.type === "QUERY" ? "feedback" : "assignment",
        selectedStudentId: record.student.id,
        selectedCardId: isSubmissionAssignmentObject(record.assignment)
          ? record.assignment.id
          : record.id,
        assignmentType: assignType === "UNIT" ? "unit" : "general",
        lessonId:
          (isSubmissionAssignmentObject(record.assignment)
            ? record.assignment.lesson?.id
            : null) ||
          record.lesson?.id ||
          null,
        unitId:
          (isSubmissionAssignmentObject(record.assignment)
            ? record.assignment.unit?.id
            : null) ||
          record.unit?.id ||
          null,
      },
    });
  };

  // Handle export of time entries as Excel or PDF
  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      // Create query params from current filters
      const queryParams: DownloadSubmissionQuery = {
        search: searchValue || undefined,
        fromDate: fromDate,
        toDate: toDate,
        ...toRecordFilterQuery(selectedListType, grade),
      };

      const response = await downloadSubmissionService(type, queryParams);

      downloadFileFromBlob(response, type, "submission_report");

      successToast(
        "Export Successful",
        `Submissions exported as ${FileFormats[type].name} successfully`
      );
    } catch (error) {
      console.error(`Error exporting submissions as ${type}:`, error);
    }
  };

  const exportOptions: ExportOption[] = [
    {
      label: "PDF",
      icon: <PDFIcon />,
      action: () => handleExport("pdf"),
    },
    {
      label: "Excel",
      icon: <ExcelIcon />,
      action: () => handleExport("xl"),
    },
    {
      label: "CSV",
      icon: <CSVIcon />,
      action: () => handleExport("csv"),
    },
  ];

  // Handle date range changes from the ListHeader component
  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // The page change will trigger a fetch
    }
  };

  useEffect(() => {
    if (selectedListType === "all") {
      setGrades([]);
      return;
    }

    const fetchResources = async () => {
      try {
        const { data } = await getAssignResourceDropdownV2Service(
          selectedListType,
          { schoolId: user?.school_id }
        );
        setGrades(
          mapCsListDropdownToGradeOptions(data.data).map((item) => ({
            id: item.id,
            label: item.grade,
          }))
        );
      } catch (error) {
        console.error("Error fetching resources:", error);
        setGrades([]);
      }
    };

    fetchResources();
  }, [selectedListType, user?.school_id]);

  const handleListTypeChange = (type: StudentListType) => {
    setSelectedListType(type);
    setGrade("");
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Handle sorting changes
  const handleSort = (
    config: { key: string; direction: "asc" | "desc" } | null
  ) => {
    setSortConfig(config);
    // Reset to first page when sort changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Map column keys to API sort fields
  const getSortField = (key: string): string => {
    const sortFieldMap: Record<string, string> = {
      id: "REG", // Unique ID column
      name: "STU", // Student Name column
      grade: "GRDE", // Grade column
      lesson: "LSN", // Lesson Name column
      unit: "UNT", // Unit Name column
      status: "STS", // Assignment Status column
      date_time: "CDT", // Submission Date & Time column
      type: "TYP", // Type column
    };

    return sortFieldMap[key] || key;
  };

  // Fetch submissions on component mount and when search, pagination, date range, or sorting changes
  useEffect(() => {
    // Reset to first page when search value changes
    if (searchValue && currentPage !== 1) {
      setCurrentPage(1);
      return; // The page change will trigger another fetch
    }

    const sortParams = sortConfig
      ? {
          sortBy: getSortField(sortConfig.key),
        }
      : {};

    dispatch(
      fetchSubmissions({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        search: searchValue,
        fromDate: fromDate,
        toDate: toDate,
        ...toRecordFilterQuery(selectedListType, grade),
        ...sortParams,
      })
    );
  }, [
    dispatch,
    currentPage,
    searchValue,
    fromDate,
    toDate,
    grade,
    selectedListType,
    sortConfig,
  ]);

  if (noSubmissions) {
    return (
      <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
        <div
          className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
        >
          {loading ? (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Loading...
              </p>
            </>
          ) : (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
                No submissions received.
              </p>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Student submissions will appear here once available.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden h-full flex flex-1 flex-col">
      <ListHeader
        title={"Submission List"}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onDateRangeChange={handleDateRangeChange}
        leftAction={
          <>
            <StudentTypeSelect
              value={selectedListType}
              onChange={handleListTypeChange}
              className="w-full shrink-0 sm:w-[200px]"
            />
            {selectedListType !== "all" && (
              <FilterWithRadio
                className="w-full shrink-0 sm:w-[250px]"
                showAbove={false}
                options={grades.map((item) => ({
                  label: item.label,
                  value: item.id,
                }))}
                value={grade}
                onChange={setGrade}
                placeholder={
                  selectedListType === "course" ? "All courses" : "All classes"
                }
              />
            )}
          </>
        }
        rightAction={
          <>
            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_EXPORT_SUBMISSION,
              userRole?.permissions as string[]
            ) && (
              <ExportDropdown
                options={exportOptions}
                buttonClassName="flex h-[48px] w-full items-center justify-between gap-2 rounded-lg border border-[#5C88C5] px-2 py-[14px] text-[14px] font-semibold text-[#5C88C5] focus:outline-none sm:w-[148px] sm:px-[16px]"
              />
            )}
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          <Table
            columns={columns}
            data={submissions} // Use data from Redux store
            rowKey="id"
            showHeader={true}
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading} // We're handling loading state ourselves
            sortConfig={sortConfig || undefined}
            onSort={handleSort}
          />
        </div>
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems} // Use actual total items from data
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Submissions"
      />
    </div>
  );
}
